import { useCallback, useEffect, useRef } from 'react';
import {
  markNotificationRangeAsRead,
  updateUserNotificationsCount,
} from '../../../assets/services/notifications';
import {
  discardPendingNotificationReads,
  enqueueNotificationRead,
} from './notificationReadQueue';

export function useNotificationReadBatcher({
  notifications,
  notificationService,
  setCurrentUser,
  setNotifications,
  unreadCount,
}) {
  const notificationsRef = useRef(notifications);
  const unreadCountRef = useRef(unreadCount);
  const observedReadIdsRef = useRef(new Set());

  const decrementUnreadCount = useCallback((readCount) => {
    if (!readCount) return;

    setCurrentUser((currentUser) => updateUserNotificationsCount(
      currentUser,
      (currentCount) => currentCount - readCount,
      unreadCountRef.current,
    ));
  }, [setCurrentUser]);

  const applyReadRange = useCallback((range) => {
    const result = markNotificationRangeAsRead(notificationsRef.current, range);

    if (!result.readCount) return;

    notificationsRef.current = result.notifications;
    setNotifications(result.notifications);
    decrementUnreadCount(result.readCount);
  }, [decrementUnreadCount, setNotifications]);

  const markNotificationViewed = useCallback((notificationId) => {
    if (!notificationId || observedReadIdsRef.current.has(notificationId)) return;

    observedReadIdsRef.current.add(notificationId);
    const range = enqueueNotificationRead(
      notificationService,
      notificationId,
      notificationsRef.current,
    );

    if (range) {
      applyReadRange(range);
    }
  }, [applyReadRange, notificationService]);

  const discardPendingReads = useCallback(() => {
    discardPendingNotificationReads(notificationService);
  }, [notificationService]);

  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  useEffect(() => {
    unreadCountRef.current = unreadCount;
  }, [unreadCount]);

  return {
    discardPendingReads,
    markNotificationViewed,
    observedReadIdsRef,
  };
}
